import { z } from 'zod';

/**
 * Scenario field catalog for the sheet-lender side of Mana Pricing (originally the "Other
 * Lenders Pricing" module, retired 2026-10-11) — the inputs the lender engines (FCM,
 * PennyMac, …) price from. Values are display
 * strings so saved scenarios and borrower emails stay human-readable. Field ids
 * are stable: saved scenarios are keyed by them.
 */

export interface FieldOption {
  value: string;
  label: string;
}
export type PricingFieldType = 'text' | 'money' | 'percent' | 'number' | 'select' | 'multiselect' | 'checkbox';
export type PricingFieldGroup = 'loan' | 'borrower' | 'property' | 'product' | 'dscr';
/** A scenario input value: display string, checkbox, or the picked options of a multiselect. */
export type PricingFieldValue = string | boolean | string[];

export interface PricingField {
  id: string;
  label: string;
  group: PricingFieldGroup;
  type: PricingFieldType;
  /** Static option list (select / multiselect only). */
  options?: string[];
  /** Options that depend on another field's value (e.g. Loan Purpose by Loan Type). */
  optionsBy?: { field: string; map: Record<string, string[]>; fallback: string[] };
  /** Only render when the named field has one of these values. */
  showWhen?: { field: string; in: string[] };
  default?: PricingFieldValue;
  placeholder?: string;
  required?: boolean;
  hint?: string;
}

/** Loan terms the scenario can price at once (the lender sheets price 10–30 years). */
export const TERM_OPTIONS = ['40 Years', '30 Years', '25 Years', '20 Years', '15 Years', '10 Years'];

/**
 * Temporary buydown programs, mirroring the Pricing module's "Compare programs", with
 * the payer spelled out: Lender-paid = the lender funds the buydown escrow and prices it
 * from its rate sheet's lender-paid buydown table (a lender whose sheet has no such
 * pricing shows the option as ineligible); Seller/Borrower-paid = the escrow is funded
 * at closing (sheet price unchanged; the cost is shown per rate). "None" = the plain
 * fixed-rate program.
 */
export const BUYDOWN_PAYER_LABEL = { lender: 'Lender-paid', borrower: 'Seller/Borrower-paid' } as const;
export const BUYDOWN_OPTIONS = [
  'None',
  '1/0 Lender-paid',
  '1/1 Lender-paid',
  '2/1 Lender-paid',
  '3/2/1 Lender-paid',
  '1/0 Seller/Borrower-paid',
  '1/1 Seller/Borrower-paid',
  '2/1 Seller/Borrower-paid',
  '3/2/1 Seller/Borrower-paid',
];
export type BuydownBase = '1-0' | '1-1' | '2-1' | '3-2-1';
export type BuydownPayer = 'borrower' | 'lender';
export interface BuydownChoice {
  /** Stable short id used in option ids, e.g. "2/1 LP" / "2/1 BP". */
  key: string;
  /** Display label as offered in the dropdown, e.g. "2/1 Lender-paid". */
  label: string;
  base: BuydownBase;
  paidBy: BuydownPayer;
  /** Rate reduction per buydown year, in percentage points. */
  steps: number[];
}
export const BUYDOWN_STEPS: Record<BuydownBase, number[]> = { '1-0': [1], '1-1': [1, 1], '2-1': [2, 1], '3-2-1': [3, 2, 1] };

/**
 * "2/1 Lender-paid" → choice; "None"/blank → null. Also accepts the earlier spellings
 * ("2/1 LLPA" / "2/1 LP" = lender-paid, "2/1 BP" = seller/borrower-paid) and a legacy
 * bare "2/1" (pre-multiselect), which meant lender-paid.
 */
export function parseBuydownOption(raw: unknown): BuydownChoice | null {
  const s = String(raw ?? '').trim();
  if (!s || /^none$/i.test(s)) return null;
  const m = s.match(/^(3\s*[/-]\s*2\s*[/-]\s*1|2\s*[/-]\s*1|1\s*[/-]\s*1|1\s*[/-]\s*0)\s*[·-]?\s*(BP|LLPA|LP|Lender[- ]paid|Seller\/Borrower[- ]paid|Seller[- ]paid|Borrower[- ]paid)?$/i);
  if (!m) return null;
  const base = m[1]!.replace(/\s/g, '').replace(/\//g, '-') as BuydownBase;
  const payer = (m[2] ?? '').toLowerCase();
  const paidBy: BuydownPayer = payer === 'bp' || /seller|borrower/.test(payer) ? 'borrower' : 'lender';
  const ratio = base.replace(/-/g, '/');
  return {
    key: `${ratio} ${paidBy === 'borrower' ? 'BP' : 'LP'}`,
    label: `${ratio} ${BUYDOWN_PAYER_LABEL[paidBy]}`,
    base,
    paidBy,
    steps: BUYDOWN_STEPS[base],
  };
}

/** Normalize a saved/submitted multiselect value: array → strings, single string → [string], blank → []. */
export function fieldValueList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x ?? '').trim()).filter(Boolean);
  const s = String(v ?? '').trim();
  return s ? [s] : [];
}

/** Coerce a raw saved value into the shape a field expects (legacy single values → lists). */
export function coerceFieldValue(field: PricingField, raw: unknown): PricingFieldValue {
  if (field.type === 'checkbox') return raw === true || /^(true|yes|1)$/i.test(String(raw ?? ''));
  if (field.type === 'multiselect') {
    const list = fieldValueList(raw);
    if (field.id === 'buydown') return list.map((x) => parseBuydownOption(x)?.label ?? (/^none$/i.test(x) ? 'None' : x));
    return list;
  }
  return String(raw ?? '');
}

/** "Conforming 30 Yr Fixed" + term/buydown → the borrower-facing option name ("… (25-yr) · 2/1 LLPA buydown"). */
export function lenderOptionName(product: string, termYears: number, buydownLabel: string | null | undefined): string {
  const hasTerm = new RegExp(`\\b${termYears}\\s*(yr|year)`, 'i').test(product);
  return `${product}${hasTerm ? '' : ` (${termYears}-yr)`}${buydownLabel ? ` · ${buydownLabel} buydown` : ''}`;
}

/**
 * Seller/borrower-paid buydown escrow cost: Σ years (note P&I − bought-down P&I) × 12.
 * Same formula as the Pricing module's concession buydown (UWM's Buydown Calculator).
 */
export function buydownEscrowCost(noteRatePct: number, termYears: number, loanAmount: number, steps: number[]): number {
  const n = Math.round(termYears * 12);
  if (n <= 0 || loanAmount <= 0) return 0;
  const pmt = (annualPct: number) => {
    const r = annualPct / 1200;
    return r <= 0 ? loanAmount / n : (loanAmount * r) / (1 - Math.pow(1 + r, -n));
  };
  const note = pmt(noteRatePct);
  return steps.reduce((sum, step) => sum + (note - pmt(noteRatePct - step)) * 12, 0);
}

export const FIELD_GROUP_LABELS: Record<PricingFieldGroup, string> = {
  loan: 'Loan',
  borrower: 'Borrower',
  property: 'Property',
  product: 'Pricing',
  dscr: 'DSCR',
};

/** Loan types the rate-sheet adapters know about (Conventional and HELOC / home equity priced today; the rest as sheets are wired in). */
export const LOAN_TYPES = ['Conventional', 'FHA', 'VA', 'USDA', 'Jumbo', 'Non-QM', 'DSCR', 'HELOC'];
/** Loan types that are first-lien mortgages (the buydown / amortization fields apply). */
const MORTGAGE_LOAN_TYPES = LOAN_TYPES.filter((t) => t !== 'HELOC');
/** Temporary buydowns: every first-lien type but DSCR (no lender offers a buydown on a DSCR loan). */
const BUYDOWN_LOAN_TYPES = MORTGAGE_LOAN_TYPES.filter((t) => t !== 'DSCR');
const DSCR_ONLY = { field: 'loanType', in: ['DSCR'] };
export const PREPAY_STRUCTURE_OPTIONS = ['Declining / 6 months interest', 'Flat'];
export const DSCR_RESIDENCY_OPTIONS = ['US Citizen', 'Permanent Resident', 'Non-Permanent Resident', 'Foreign National'];
/** The DSCR scenario inputs, in panel order (the "DSCR" section shows them for a DSCR loan). */
export const DSCR_FIELD_IDS = ['dscrRatio', 'monthlyRent', 'monthlyPitia', 'prepaymentPenalty', 'prepaymentStructure', 'interestOnly', 'shortTermRental', 'condoType', 'residency'];

/** DSCR = gross monthly rent ÷ PITIA, two decimals ("" when either is missing). */
export function dscrFromRent(rent: unknown, pitia: unknown): string {
  const n = (v: unknown) => Number(String(v ?? '').replace(/[$,\s]/g, ''));
  const r = n(rent);
  const p = n(pitia);
  return r > 0 && p > 0 ? (Math.floor((r / p) * 100) / 100).toFixed(2) : '';
}
/** Home-equity products a HELOC / second-lien scenario prices (Spring EQ's sheet has all three). */
export const HOME_EQUITY_PRODUCT_OPTIONS = ['All', 'Fixed-rate loan (HELOAN)', 'Variable-rate HELOC', 'Fixed-rate HELOC'];
/** "Standalone" = a new second lien behind the existing first; purchase / rate-term = a piggyback closing with the first mortgage. */
export const HOME_EQUITY_PURPOSES = ['Standalone (existing 1st stays)', 'Purchase (piggyback)', 'Rate/Term Refinance (piggyback)', 'Refi Cash-Out'];

export const AMORTIZATION_OPTIONS = ['Fixed', 'ARM'];

/**
 * The scenario's loan types, in pick order (Loan Type is a multi-select; a legacy single value
 * still reads). Empty → Conventional, the field's default.
 */
export function loanTypesOf(inputs: Record<string, unknown> | null | undefined): string[] {
  const list = fieldValueList(inputs?.loanType);
  const known = list.map((v) => LOAN_TYPES.find((t) => t.toLowerCase() === v.toLowerCase()) ?? v);
  return known.length ? [...new Set(known)] : ['Conventional'];
}

/** The scenario's amortizations ("Fixed" / "ARM"), in pick order. Empty → Fixed, the field's default. */
export function amortizationsOf(inputs: Record<string, unknown> | null | undefined): Array<'Fixed' | 'ARM'> {
  const out: Array<'Fixed' | 'ARM'> = [];
  for (const v of fieldValueList(inputs?.amortizationType)) {
    const a = /arm/i.test(v) ? 'ARM' : 'Fixed';
    if (!out.includes(a)) out.push(a);
  }
  return out.length ? out : ['Fixed'];
}

/**
 * Every loan type × amortization the scenario asks for, as single-valued overlays of the
 * scenario — each is priced exactly like a scenario that picked only that pair, so every
 * engine, eligibility rule and ★ comp category keeps working on one loan type at a time.
 */
export function firstLienCombos(inputs: Record<string, unknown>): Array<{ loanType: string; amortizationType: 'Fixed' | 'ARM' }> {
  const out: Array<{ loanType: string; amortizationType: 'Fixed' | 'ARM' }> = [];
  for (const loanType of loanTypesOf(inputs)) {
    if (/^heloc$/i.test(loanType)) continue;
    for (const amortizationType of amortizationsOf(inputs)) out.push({ loanType, amortizationType });
  }
  return out;
}

/** A combination's display name: "Conventional", "FHA ARM", "DSCR Fixed" (the amortization only when both are picked). */
export function comboLabel(combo: { loanType: string; amortizationType: string }, bothAmortizations: boolean): string {
  return bothAmortizations ? `${combo.loanType} ${combo.amortizationType}` : combo.amortizationType === 'ARM' ? `${combo.loanType} ARM` : combo.loanType;
}

/** A HELOC or second-lien scenario prices on the home-equity lenders (UWM HELOC, Spring EQ), not the first-lien sheets. */
export function isHomeEquityInputs(inputs: Record<string, unknown>): boolean {
  return loanTypesOf(inputs).some((t) => /heloc/i.test(t)) || /second/i.test(String(inputs.lienPosition ?? ''));
}

const PRODUCT_TYPE_BY_LOAN_TYPE: Record<string, string[]> = {
  Conventional: ['Standard', 'HomeReady', 'Home Possible', 'HomeOne', 'RefiNow', 'Refi Possible'],
  FHA: ['Standard', 'Streamline'],
  VA: ['Standard', 'IRRRL'],
  USDA: ['Standard', 'Streamline'],
  Jumbo: ['Standard'],
  'Non-QM': ['Full Doc', 'Bank Statement', 'Asset Depletion', '1099', 'P&L'],
  DSCR: ['Standard'],
  HELOC: ['Standard'],
};
const LOAN_PURPOSE_BY_LOAN_TYPE: Record<string, string[]> = {
  Conventional: ['Purchase', 'Rate/Term Refinance', 'Refi Cash-Out'],
  FHA: ['Purchase', 'Rate/Term Refinance', 'Refi Cash-Out', 'Streamline Refinance'],
  VA: ['Purchase', 'Refi Cash-Out', 'IRRRL'],
  USDA: ['Purchase', 'Rate/Term Refinance', 'Streamline Refinance'],
  Jumbo: ['Purchase', 'Rate/Term Refinance', 'Refi Cash-Out'],
  'Non-QM': ['Purchase', 'Rate/Term Refinance', 'Refi Cash-Out'],
  DSCR: ['Purchase', 'Rate/Term Refinance', 'Refi Cash-Out'],
  HELOC: HOME_EQUITY_PURPOSES,
};

export const PRICING_FIELDS: PricingField[] = [
  // ---------------- Loan ----------------
  {
    id: 'loanType',
    label: 'Loan Type',
    group: 'loan',
    type: 'multiselect',
    options: LOAN_TYPES,
    default: ['Conventional'],
    required: true,
    hint: 'Pick one or more — each is priced on every lender that offers it (the rate sheets price Conventional, Jumbo and DSCR; UWM prices Conventional, FHA, VA and USDA). HELOC prices on its own.',
  },
  { id: 'lienPosition', label: 'Lien Position', group: 'loan', type: 'select', options: ['First', 'Second'], default: 'First' },
  {
    id: 'loanPurpose',
    label: 'Loan Purpose',
    group: 'loan',
    type: 'select',
    optionsBy: { field: 'loanType', map: LOAN_PURPOSE_BY_LOAN_TYPE, fallback: ['Purchase', 'Rate/Term Refinance', 'Refi Cash-Out'] },
    default: 'Purchase',
    required: true,
  },
  {
    id: 'productType',
    label: 'Program',
    group: 'loan',
    type: 'select',
    optionsBy: { field: 'loanType', map: PRODUCT_TYPE_BY_LOAN_TYPE, fallback: ['Standard'] },
    default: 'Standard',
  },
  { id: 'propertyValue', label: 'Property Value', group: 'loan', type: 'money', required: true, placeholder: '625000' },
  { id: 'baseLoanAmount', label: 'Base Loan Amount', group: 'loan', type: 'money', required: true, placeholder: '500000', hint: 'For a HELOC: the line amount.' },
  { id: 'ltv', label: 'LTV', group: 'loan', type: 'percent', hint: 'Auto-calculates with loan amount + property value.' },
  // Home equity (HELOC / second lien): the senior balance sets the CLTV the home-equity lenders price on.
  {
    id: 'seniorLienBalance',
    label: '1st Mortgage Balance',
    group: 'loan',
    type: 'money',
    placeholder: '350000',
    showWhen: { field: 'lienPosition', in: ['Second'] },
    hint: 'Balance of the existing (or new piggyback) first mortgage — sets the CLTV. Blank = no senior lien.',
  },
  { id: 'cltv', label: 'CLTV', group: 'loan', type: 'percent', showWhen: { field: 'lienPosition', in: ['Second'] }, hint: 'Auto-calculates: (loan + 1st mortgage balance) ÷ property value.' },
  {
    id: 'initialDraw',
    label: 'Initial Draw',
    group: 'loan',
    type: 'money',
    showWhen: { field: 'loanType', in: ['HELOC'] },
    hint: 'HELOC / fixed-rate HELOC: amount drawn at closing (blank = the full line). Lender minimums apply.',
  },
  {
    id: 'homeEquityProduct',
    label: 'Home Equity Product',
    group: 'loan',
    type: 'select',
    options: HOME_EQUITY_PRODUCT_OPTIONS,
    default: 'All',
    showWhen: { field: 'loanType', in: ['HELOC'] },
    hint: 'Which home-equity products to price: a fixed-rate home equity loan (closed-end second), a variable HELOC (prime + margin, 3-year draw) or a fixed-rate HELOC.',
  },
  {
    id: 'term',
    label: 'Term',
    group: 'loan',
    type: 'multiselect',
    options: TERM_OPTIONS,
    default: ['30 Years'],
    required: true,
    hint: 'Pick one or more terms — every selected term is priced (the lender sheets price 10–30 years).',
  },
  {
    id: 'amortizationType',
    label: 'Amortization',
    group: 'loan',
    type: 'multiselect',
    options: AMORTIZATION_OPTIONS,
    default: ['Fixed'],
    required: true,
    showWhen: { field: 'loanType', in: MORTGAGE_LOAN_TYPES },
    hint: 'Pick Fixed, ARM or both — each is priced as its own set of options.',
  },
  { id: 'armType', label: 'ARM Type', group: 'loan', type: 'select', options: ['All', '5/6', '7/6', '10/6'], default: 'All', showWhen: { field: 'amortizationType', in: ['ARM'] } },
  { id: 'subordinateFinancing', label: 'Subordinate financing', group: 'loan', type: 'checkbox', default: false },
  {
    id: 'subordinateAmount',
    label: 'Subordinate lien amount',
    group: 'loan',
    type: 'money',
    placeholder: '30000',
    showWhen: { field: 'subordinateFinancing', in: ['true'] },
    hint: 'The second lien behind this loan — the lenders price subordinate financing on the combined LTV.',
  },
  // VA-only extras.
  { id: 'vaExemptFundingFee', label: 'Exempt from Funding Fee', group: 'loan', type: 'checkbox', default: false, showWhen: { field: 'loanType', in: ['VA'] } },
  { id: 'vaSubsequentUse', label: 'Subsequent Use', group: 'loan', type: 'checkbox', default: false, showWhen: { field: 'loanType', in: ['VA'] } },

  // ---------------- Borrower ----------------
  { id: 'creditScore', label: 'Credit Score', group: 'borrower', type: 'number', required: true, placeholder: '780' },
  { id: 'dtiRatio', label: 'DTI Ratio', group: 'borrower', type: 'percent', placeholder: '36' },
  {
    id: 'firstTimeHomeBuyer',
    label: 'First-time homebuyer',
    group: 'borrower',
    type: 'checkbox',
    default: false,
    hint: 'At least one borrower is a first-time homebuyer — the agencies require it on a purchase above 95% LTV and for HomeOne.',
  },
  { id: 'fthbAmiWaiver', label: 'First-time buyer within AMI limits', group: 'borrower', type: 'checkbox', default: false, hint: 'Agency LLPA waiver (≤ 100% AMI, or 120% in high-cost areas). Implies first-time homebuyer.' },
  { id: 'monthlyQualifyingIncome', label: 'Monthly Qualifying Income', group: 'borrower', type: 'money' },

  // ---------------- Property ----------------
  { id: 'occupancy', label: 'Occupancy', group: 'property', type: 'select', options: ['Primary Residence', 'Second Home', 'Investment Property'], default: 'Primary Residence', required: true },
  { id: 'legalStructure', label: 'Property Type', group: 'property', type: 'select', options: ['Single Family', 'Condo', 'PUD'], default: 'Single Family' },
  { id: 'attachmentType', label: 'Attached / Detached', group: 'property', type: 'select', options: ['Detached', 'Attached'], default: 'Detached' },
  { id: 'numberOfUnits', label: 'Units', group: 'property', type: 'select', options: ['1 Unit', '2 Units', '3 Units', '4 Units'], default: '1 Unit' },
  { id: 'manufactured', label: 'Manufactured', group: 'property', type: 'select', options: ['No', 'Single-Wide', 'Double-Wide', 'Single Wide - MH Advantage/CHOICEHome', 'Double Wide - MH Advantage/CHOICEHome'], default: 'No' },
  { id: 'propertyZip', label: 'Property ZIP', group: 'property', type: 'text', required: true, placeholder: '75078' },
  { id: 'state', label: 'State', group: 'property', type: 'text', placeholder: 'TX', hint: 'Derived from the ZIP when blank.' },
  { id: 'county', label: 'County', group: 'property', type: 'text', placeholder: 'Collin' },

  // ---------------- Pricing ----------------
  { id: 'desiredLockPeriod', label: 'Lock Period (days)', group: 'product', type: 'select', options: ['15', '30', '45', '60', '75', '90'], default: '30' },
  { id: 'desiredPrice', label: 'Desired Price', group: 'product', type: 'text', default: '100.000', hint: 'Headline rate = the one priced closest to this.' },
  {
    id: 'compType',
    label: 'Comp',
    group: 'product',
    type: 'select',
    options: ['LP', 'BP'],
    default: 'LP',
    hint: 'LP = lender-paid: prices shown net of each lender’s comp. BP = borrower-paid: gross sheet price, comp paid by the borrower.',
  },
  {
    id: 'buydown',
    label: 'Temporary Buydown',
    group: 'product',
    type: 'multiselect',
    options: BUYDOWN_OPTIONS,
    default: ['None'],
    required: true,
    showWhen: { field: 'loanType', in: BUYDOWN_LOAN_TYPES },
    hint: 'Pick the programs to compare. Lender-paid = the lender funds the buydown and prices it from its rate sheet (lenders without that pricing show the option as ineligible). Seller/Borrower-paid = escrow funded at closing; sheet price unchanged, cost shown per rate.',
  },
  { id: 'waiveEscrows', label: 'Waive Escrows', group: 'product', type: 'select', options: ['No', 'Yes'], default: 'No', showWhen: { field: 'loanType', in: MORTGAGE_LOAN_TYPES } },
  {
    id: 'prepaymentPenalty',
    label: 'Prepayment Penalty',
    group: 'product',
    type: 'select',
    options: ['None', '1 Year', '2 Year', '3 Year', '4 Year', '5 Year'],
    default: 'None',
    showWhen: { field: 'loanType', in: ['Non-QM', 'DSCR'] },
    hint: 'A longer prepayment penalty improves the price on every DSCR lender. Some states don\'t allow one (e.g. PennyMac: AK, MD, MI, MN, NM).',
  },
  { id: 'interestOnly', label: 'Interest Only', group: 'product', type: 'select', options: ['No', 'Yes'], default: 'No', showWhen: { field: 'loanType', in: ['Non-QM', 'DSCR', 'Jumbo'] } },

  // ---------------- DSCR (investor cash-flow loans) ----------------
  {
    id: 'dscrRatio',
    label: 'DSCR',
    group: 'dscr',
    type: 'number',
    required: true,
    placeholder: '1.25',
    showWhen: DSCR_ONLY,
    hint: 'Debt service coverage ratio = gross monthly rent ÷ PITIA. Fills in from the rent and PITIA; each lender prices it in its own DSCR bands (most need 0.75 or more, 1.00+ prices best).',
  },
  { id: 'monthlyRent', label: 'Monthly Rent', group: 'dscr', type: 'money', placeholder: '2500', showWhen: DSCR_ONLY, hint: 'Gross monthly rent: the lease, or the appraiser\'s market rent (1007).' },
  { id: 'monthlyPitia', label: 'Monthly PITIA', group: 'dscr', type: 'money', placeholder: '2000', showWhen: DSCR_ONLY, hint: 'Principal & interest + taxes + insurance + HOA at the expected rate.' },
  {
    id: 'prepaymentStructure',
    label: 'Prepay Structure',
    group: 'dscr',
    type: 'select',
    options: PREPAY_STRUCTURE_OPTIONS,
    default: PREPAY_STRUCTURE_OPTIONS[0],
    showWhen: { field: 'prepaymentPenalty', in: ['1 Year', '2 Year', '3 Year', '4 Year', '5 Year'] },
    hint: 'PennyMac prices a flat penalty on its own table; the other lenders price every structure alike.',
  },
  { id: 'shortTermRental', label: 'Short-term rental', group: 'dscr', type: 'checkbox', default: false, showWhen: DSCR_ONLY, hint: 'Rent from Airbnb / VRBO-style short-term stays.' },
  { id: 'condoType', label: 'Condo Type', group: 'dscr', type: 'select', options: ['Warrantable', 'Non-warrantable', 'Condotel'], default: 'Warrantable', showWhen: { field: 'legalStructure', in: ['Condo'] } },
  { id: 'residency', label: 'Borrower Residency', group: 'dscr', type: 'select', options: DSCR_RESIDENCY_OPTIONS, default: 'US Citizen', showWhen: DSCR_ONLY },
];

/**
 * Resolve the option list for a field given the current form values. When the driving field is
 * a multi-select (Loan Type), the options are the union across its picks, in pick order — each
 * loan type then prices the choice its own way (a purpose it doesn't offer is noted, not sent).
 */
export function fieldOptionsFor(field: PricingField, values: Record<string, unknown>): string[] {
  if (field.optionsBy) {
    const drivers = fieldValueList(values[field.optionsBy.field]);
    const out: string[] = [];
    for (const d of drivers) for (const o of field.optionsBy.map[d] ?? field.optionsBy.fallback) if (!out.includes(o)) out.push(o);
    return out.length ? out : field.optionsBy.fallback;
  }
  return field.options ?? [];
}

/**
 * Whether a conditional field should render for the current values (a chain of showWhen rules
 * must all hold). A multi-select driver shows the field when any of its picks qualifies.
 */
export function fieldVisible(field: PricingField, values: Record<string, unknown>): boolean {
  if (!field.showWhen) return true;
  const picks = fieldValueList(values[field.showWhen.field]);
  if (!picks.some((p) => field.showWhen!.in.includes(p))) return false;
  const parent = PRICING_FIELDS.find((f) => f.id === field.showWhen!.field);
  return parent ? fieldVisible(parent, values) : true;
}

/** Default values for a fresh scenario. */
export function fieldDefaults(): Record<string, PricingFieldValue> {
  const out: Record<string, PricingFieldValue> = {};
  for (const f of PRICING_FIELDS) {
    const d = f.default;
    out[f.id] = Array.isArray(d) ? [...d] : (d ?? (f.type === 'checkbox' ? false : f.type === 'multiselect' ? [] : ''));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Borrower-facing rate options (built from a sheet price point, or entered by hand
// for a lender that has no sheet adapter yet).
// ---------------------------------------------------------------------------

export const LenderRateRowSchema = z.object({
  lender: z.string().max(120).default(''),
  product: z.string().max(160).default(''),
  rate: z.string().max(40).default(''),
  points: z.string().max(40).default(''),
  monthlyPayment: z.string().max(40).default(''),
  lenderCredit: z.string().max(40).default(''),
  notes: z.string().max(300).default(''),
  /** Lender product id when the row came from a rate sheet (re-priceable); absent for manual rows. */
  productId: z.string().max(200).optional(),
});
export type LenderRateRow = z.infer<typeof LenderRateRowSchema>;

export const LENDER_RATE_COLUMNS: Array<{ id: keyof LenderRateRow; label: string; width: string }> = [
  { id: 'lender', label: 'Lender / Investor', width: 'w-40' },
  { id: 'product', label: 'Product', width: 'w-44' },
  { id: 'rate', label: 'Rate', width: 'w-24' },
  { id: 'points', label: 'Points / Price', width: 'w-28' },
  { id: 'monthlyPayment', label: 'Monthly P&I', width: 'w-28' },
  { id: 'lenderCredit', label: 'Lender Credit', width: 'w-28' },
  { id: 'notes', label: 'Notes', width: 'w-48' },
];

export function emptyLenderRateRow(): LenderRateRow {
  return { lender: '', product: '', rate: '', points: '', monthlyPayment: '', lenderCredit: '', notes: '' };
}

/**
 * One borrower-facing option built from a rate-sheet price point. Used by the
 * web tray ("+ Add") AND the recurring-email scheduler so both produce identical
 * rows — keep the formatting here, not in either caller.
 */
export function buildLenderRateEntry(o: {
  productId: string;
  investor: string;
  product: string;
  rate: number;
  price: number;
  apr: number;
  pi: number;
  discountDollars: number; // + = lender credit to the borrower, − = discount points cost
  lockPeriod: number;
  lastUpdated: string | null;
  sheetVersion: string;
  /** e.g. "LP comp 1.000%" or "BP comp 1.000%" — how the broker comp was priced. */
  comp?: string | null;
  /** Extra note, e.g. the seller-paid buydown escrow cost. */
  extraNote?: string | null;
  /** When the rate was priced, formatted for the borrower (e.g. "Sep 11, 2026, 1:30 PM CDT"); replaces the sheet label. */
  asOf?: string | null;
}): LenderRateRow {
  const money0 = (v: number) => v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const credit = o.discountDollars;
  return {
    productId: o.productId,
    lender: o.investor,
    product: o.product,
    rate: `${o.rate.toFixed(3)}%`,
    points: o.price.toFixed(3),
    monthlyPayment: money0(o.pi),
    lenderCredit: credit >= 0 ? money0(credit) : `(${money0(-credit)})`,
    notes: `${o.lockPeriod}-day lock · APR ${o.apr.toFixed(3)}%${o.comp ? ` · ${o.comp}` : ''}${o.extraNote ? ` · ${o.extraNote}` : ''} · ${o.asOf ? `rate as of ${o.asOf}` : `sheet ${o.lastUpdated ?? o.sheetVersion}`}`,
  };
}

/** "Sep 11, 2026, 1:30 PM CDT" — when a rate was priced, in Central time (borrower emails, tray notes). */
export function fmtRateAsOf(iso: string | Date | null | undefined): string | null {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Chicago',
    timeZoneName: 'short',
  });
}

// ---------------------------------------------------------------------------
// API payloads
// ---------------------------------------------------------------------------

/** Products the MLO picked for the borrower (results tray) — keyed by lender product id. */
export const LenderSelectionSchema = z.array(z.string().min(1).max(200)).max(25);

export const LenderScenarioSaveSchema = z.object({
  borrowerName: z.string().trim().min(1).max(200),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().max(40).optional().or(z.literal('')),
  inputs: z.record(z.unknown()),
  rates: z.array(LenderRateRowSchema).max(25).optional(),
  selection: LenderSelectionSchema.optional(),
});
export type LenderScenarioSave = z.infer<typeof LenderScenarioSaveSchema>;

/**
 * A lender-sheet quote request (Mana Pricing's sheet pass; originally the body of the retired
 * Other Lenders Pricing module's POST /quote). The borrower name is the save key: every
 * "Get pricing" upserts the scenario BEFORE pricing, so a failed quote never loses the
 * borrower's inputs.
 */
export const LenderQuoteRequestSchema = z.object({
  borrowerName: z.string().trim().min(1).max(200),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().max(40).optional().or(z.literal('')),
  inputs: z.record(z.unknown()),
  selection: LenderSelectionSchema.optional(),
});
export type LenderQuoteRequest = z.infer<typeof LenderQuoteRequestSchema>;

export const LenderEmailSchema = z.object({
  to: z.string().email(),
  borrowerName: z.string().trim().min(1).max(200),
  inputs: z.record(z.unknown()),
  rates: z.array(LenderRateRowSchema).min(1).max(25),
  ratesAsOf: z.string().datetime().nullable().optional(),
});
export type LenderEmailInput = z.infer<typeof LenderEmailSchema>;

// Recurring borrower emails (Other Lenders) — one per borrower per MLO. Each run
// re-prices the frozen inputs on the CURRENT lender sheets for the picked products.
export const LENDER_SCHEDULE_MIN_INTERVAL_MINUTES = 240; // 4h floor
export const LENDER_SCHEDULE_MAX_INTERVAL_MINUTES = 525_600; // 1 year

export const LenderScheduleCreateSchema = z.object({
  borrowerName: z.string().trim().min(1).max(200),
  toEmail: z.string().email(),
  inputs: z.record(z.unknown()),
  /** Lender product ids to include; empty = every eligible product. */
  productIds: LenderSelectionSchema,
  intervalMinutes: z.number().int().min(LENDER_SCHEDULE_MIN_INTERVAL_MINUTES).max(LENDER_SCHEDULE_MAX_INTERVAL_MINUTES),
  /** Optional future start — when set, the first send happens then (not now). */
  startAt: z.string().datetime().nullable().optional(),
});
export type LenderScheduleCreate = z.infer<typeof LenderScheduleCreateSchema>;

export const LenderSchedulePatchSchema = z
  .object({
    active: z.boolean().optional(),
    startAt: z.string().datetime().nullable().optional(),
  })
  .refine((b) => b.active !== undefined || b.startAt !== undefined, { message: 'Provide active and/or startAt.' });
